/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ChangeEvent } from "react";
import debounce from "lodash.debounce";
import { When } from "react-if";
import {
  Button,
  ExtendedPaginationBox,
  FlexContainer,
  Input,
  Sidebar,
  Spacer,
  theme,
} from "@nordcloud/gnui";
import { LoaderWrap } from "~/components";
import { useApplicationsPagination } from "~/hooks";
import { noop } from "~/tools";
import { DEBOUNCE_TIMEOUT_MS } from "../constants";
import { AppEnvSelectionAccordion } from "./AppEnvSelectionAccordion";
import { AppEnvResult, ApplicationOption, EnvironmentOption } from "./types";
import { getButtonLabel } from "./utils";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  data?: AppEnvResult;
  loading: boolean;
  page: number;
  setPage: (page: number) => void;
  setApplicationName: (appName: string) => void;
  toggleApp: (appNid: string) => void;
  toggleEnv: (env: EnvironmentOption) => void;
  onApply: () => void;
  selectedApps?: ApplicationOption[];
  selectedEnvs?: EnvironmentOption[];
  disabled?: boolean;
};

export function AppEnvFilteringSidebar({
  isOpen,
  onClose,
  data,
  loading,
  page,
  setPage,
  setApplicationName,
  toggleApp,
  toggleEnv,
  onApply,
  selectedApps = [],
  selectedEnvs = [],
  disabled = false,
}: Props) {
  const { pageSize } = useApplicationsPagination();

  const debouncedSearch = debounce((searchValue: string) => {
    setPage(0);
    setApplicationName(searchValue);
  }, DEBOUNCE_TIMEOUT_MS);

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value.toLowerCase();
    debouncedSearch(searchValue);
  };

  const appsEnvs = data?.applicationsPaginated?.results ?? [];

  function SidebarFooter() {
    return (
      <FlexContainer
        direction="column"
        css={{ width: "100%", paddingBottom: theme.spacing.spacing04 }}
      >
        <div css={{ width: "100%" }}>
          <ExtendedPaginationBox
            small
            count={data?.applicationsPaginated?.count ?? 0}
            from={page * pageSize}
            setSize={noop}
            size={pageSize}
            setPage={(newPage) => {
              setPage(newPage / pageSize);
            }}
          />
        </div>
        <Spacer height={theme.spacing.spacing04} />
        <Button onClick={onApply}>
          {getButtonLabel(selectedApps, selectedEnvs)}
        </Button>
      </FlexContainer>
    );
  }

  return (
    <Sidebar
      isOpen={isOpen}
      title="Select applications and environments"
      footer={
        <When condition={!loading}>
          <SidebarFooter />
        </When>
      }
      onClick={onClose}
    >
      <Input small type="text" placeholder="Search" onChange={onSearchChange} />
      <LoaderWrap inContent loading={loading}>
        <AppEnvSelectionAccordion
          data={appsEnvs}
          selectedApps={selectedApps}
          selectedEnvs={selectedEnvs}
          disabled={disabled}
          onAppToggle={toggleApp}
          onEnvToggle={toggleEnv}
        />
      </LoaderWrap>
    </Sidebar>
  );
}
